import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Chip,
  Avatar,
  Alert,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import { Search, Add, Refresh, Home } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import MaidQuickActions from '../components/MaidQuickActions';
import MaidStatusToggle from '../components/MaidStatusToggle';
import OnboardingStatusChip from '../components/OnboardingStatusChip';
import EditableMaidDetailsModal from '../components/EditableMaidDetailsModal';
import InviteMaidDialog from '../components/InviteMaidDialog';
import AdminPageLayout from '../components/admin/AdminPageLayout';
import TechnicianPortalAccess from '../components/admin/TechnicianPortalAccess';
import RequestAddressesDialog from '../components/admin/RequestAddressesDialog';
import { analyzeProfileCompletion } from '../utils/ProfileCompletionTracker';

// Completion indicator component for maid list items
const CompletionIndicator = ({ maid }) => {
  // Prefer the server-computed completion from /api/maids/light — light rows
  // don't carry the JSONB groups the client-side analyzer needs. Full maid
  // objects (modal path) fall back to local analysis.
  let completionPercentage;
  let missingCount;

  if (maid?.profileCompletion) {
    completionPercentage = maid.profileCompletion.percentage;
    missingCount = maid.profileCompletion.missingRequired;
  } else {
    const profileAnalysis = analyzeProfileCompletion(maid);
    if (!profileAnalysis) return null;
    completionPercentage = profileAnalysis.overall.completionPercentage;
    missingCount = profileAnalysis.allMissingRequired.length;
  }

  const getColor = () => {
    if (completionPercentage >= 100) return 'success';
    if (completionPercentage >= 80) return 'warning';
    return 'error';
  };

  const getTooltip = () => {
    if (missingCount === 0) {
      return `${completionPercentage}% complete - All required fields filled`;
    }
    return `${completionPercentage}% complete - ${missingCount} required fields missing`;
  };
  
  return (
    <Tooltip title={getTooltip()}>
      <Chip
        label={`${completionPercentage}%`}
        size="small"
        color={getColor()}
        variant="outlined"
        sx={{ 
          minWidth: 45,
          fontWeight: 500,
          ...(missingCount > 0 && {
            borderStyle: 'dashed'
          })
        }}
      />
    </Tooltip>
  );
};

const MaidCrewManagement = () => {
  const [maids, setMaids] = useState([]);
  // Portal access per technician user id: { lastLogin, lastInvitedAt }
  const [portalStatuses, setPortalStatuses] = useState({});
  const [filteredMaids, setFilteredMaids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedMaid, setSelectedMaid] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [aPlayerFilter, setAPlayerFilter] = useState('all');
  const [comprehensiveData, setComprehensiveData] = useState(null);
  const [loadingComprehensive, setLoadingComprehensive] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [loadingFullData, setLoadingFullData] = useState(new Set()); // Track which maids are loading full data
  
  const location = useLocation();

  // Handle success message from admin editing
  useEffect(() => {
    if (location.state?.success) {
      setSuccessMessage(location.state.message || 'Operation completed successfully');
      
      // Clear the success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // No automatic loading - user must search first

  // Search maids function - only called when user searches
  // Load sign-in / invitation status for the technicians on screen
  useEffect(() => {
    const userIds = [...new Set(maids.map((m) => m.userId || m.user?.id).filter(Boolean))];
    if (!userIds.length) return;
    let cancelled = false;
    const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
    axios.get(`${apiBase}/api/technicians/portal-status`, {
      params: { userIds: userIds.join(',') },
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((res) => { if (!cancelled) setPortalStatuses((prev) => ({ ...prev, ...(res.data?.statuses || {}) })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [maids]);

  const searchMaids = async () => {
    if (!searchTerm.trim()) {
      alert('Please enter a search term (name or email)');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
      
      const params = new URLSearchParams({
        search: searchTerm.trim(),
        limit: 100, // Get more results for management page
        sortBy: 'firstName',
        sortOrder: 'ASC',
        activeOnly: 'false' // Include all maids regardless of active status
      });
      
      if (statusFilter !== 'all') {
        params.append('onboardingStatus', statusFilter);
      }
      
      const response = await axios.get(`${apiBase}/api/maids/light?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const lightMaids = response.data?.data || [];
      
      // Transform light data to match the expected format for the UI
      const transformedMaids = lightMaids.map(maid => ({
        // Core fields from light endpoint
        id: maid.id,
        firstName: maid.firstName,
        lastName: maid.lastName,
        email: maid.email,
        phoneNumber: maid.phoneNumber,
        onboardingStatus: maid.onboardingStatus,
        hourlyRate: maid.hourlyRate,
        rating: maid.rating,
        experience: maid.experienceLevel,
        bringsSupplies: maid.hasSupplies,
        
        // Additional fields that may be used by UI components
        profilePicture: maid.profilePicture,
        residentialAddress: maid.residentialAddress,
        schedulePreferences: maid.schedulePreferences,
        // Server-computed completion for the list chip (light rows lack the
        // JSONB data the client-side analyzer would need)
        profileCompletion: maid.profileCompletion,
        
        userId: maid.userId,
        lastLogin: maid.lastLogin,

        // User object for compatibility with existing components
        user: {
          id: maid.userId,
          firstName: maid.firstName,
          lastName: maid.lastName,
          email: maid.email,
          phoneNumber: maid.phoneNumber,
          isActive: maid.isActive
        },
        
        // Default values for fields not in light endpoint
        services: [], // Will be loaded on demand
        serviceAreas: [], // Will be loaded on demand
        languages: [], // Will be loaded on demand
        updatedAt: maid.updatedAt || new Date().toISOString(),
        isActive: maid.isActive,
        isAPlayer: maid.isAPlayer || false,
        
        // Flag to indicate this is light data
        _isLightData: true
      }));

      // Apply client-side A-Player filter if needed
      let filtered = transformedMaids;
      if (aPlayerFilter !== 'all') {
        filtered = filtered.filter(maid => {
          return aPlayerFilter === 'aplayer' ? maid.isAPlayer : !maid.isAPlayer;
        });
      }
      
      setMaids(transformedMaids);
      setFilteredMaids(filtered);
      setHasSearched(true);
    } catch (error) {
      console.error('Error searching maids:', error);
      setMaids([]);
      setFilteredMaids([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle Enter key in search field
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      searchMaids();
    }
  };

  // Handle filter changes - re-apply filters to existing results
  useEffect(() => {
    if (!hasSearched) return;
    
    let filtered = maids;
    
    // Apply A-Player filter
    if (aPlayerFilter !== 'all') {
      filtered = filtered.filter(maid => {
        return aPlayerFilter === 'aplayer' ? maid.isAPlayer : !maid.isAPlayer;
      });
    }
    
    setFilteredMaids(filtered);
  }, [maids, aPlayerFilter, hasSearched]);

  // Clear search results
  const handleClearSearch = () => {
    setSearchTerm('');
    setMaids([]);
    setFilteredMaids([]);
    setHasSearched(false);
  };

  // Fetch full maid data for a specific maid (when light data needs to be enriched)
  const fetchFullMaidData = async (maidId) => {
    // Add to loading set
    setLoadingFullData(prev => new Set([...prev, maidId]));
    
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
      const response = await axios.get(`${apiBase}/api/maids/${maidId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.data) {
        const fullMaidData = response.data.data;

        // MERGE full data over the light row instead of replacing it: the
        // list renders the flattened light fields (firstName, email,
        // phoneNumber, residentialAddress, profileCompletion...) which the
        // full API object doesn't carry — replacing wholesale blanked the
        // row after closing the modal.
        const mergeFull = (maid) => ({
          ...maid,
          ...fullMaidData,
          user: fullMaidData.user || maid.user,
          _isLightData: false // Mark as full data
        });

        setMaids(prev => prev.map(maid =>
          maid.id === maidId ? mergeFull(maid) : maid
        ));

        setFilteredMaids(prev => prev.map(maid =>
          maid.id === maidId ? mergeFull(maid) : maid
        ));

        const lightMaid = maids.find(m => m.id === maidId) || {};
        return mergeFull(lightMaid);
      }
      return null;
    } catch (error) {
      console.error('Error fetching full maid data:', error);
      return null;
    } finally {
      // Remove from loading set
      setLoadingFullData(prev => {
        const newSet = new Set(prev);
        newSet.delete(maidId);
        return newSet;
      });
    }
  };

  // Fetch comprehensive onboarding data for a specific maid
  const fetchComprehensiveData = async (maidId) => {
    setLoadingComprehensive(true);
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
      const response = await axios.get(`${apiBase}/api/maids?includeOnboardingData=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Find the specific maid's comprehensive data
      const maidWithComprehensiveData = response.data.find(m => m.id === maidId);
      if (maidWithComprehensiveData && maidWithComprehensiveData.onboardingData) {
        setComprehensiveData(maidWithComprehensiveData.onboardingData);
      } else {
        setComprehensiveData(null);
      }
    } catch (error) {
      console.error('Error fetching comprehensive data:', error);
      setComprehensiveData(null);
    } finally {
      setLoadingComprehensive(false);
    }
  };

  const handleViewDetails = async (maid) => {
    setSelectedMaid(maid);
    setModalOpen(true);
    setComprehensiveData(null); // Reset previous data
    
    // If this maid only has light data, fetch full data first
    if (maid._isLightData) {
      const fullMaidData = await fetchFullMaidData(maid.id);
      if (fullMaidData) {
        setSelectedMaid(fullMaidData);
      }
    }
    
    // Fetch comprehensive data for this maid
    fetchComprehensiveData(maid.id);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedMaid(null);
    setComprehensiveData(null);
  };

  const handleProfileUpdate = (updatedProfile) => {
    setMaids(prev => prev.map(maid => 
      maid.id === updatedProfile.id ? updatedProfile : maid
    ));
    setSelectedMaid(updatedProfile);
  };

  const handleStatusChange = (maidId, newStatus) => {
    setMaids(prev => prev.map(maid => 
      maid.id === maidId 
        ? { ...maid, isActive: newStatus, user: { ...maid.user, isActive: newStatus } }
        : maid
    ));
    if (selectedMaid?.id === maidId) {
      setSelectedMaid(prev => ({
        ...prev,
        isActive: newStatus,
        user: { ...prev.user, isActive: newStatus }
      }));
    }
  };

  const handleRefresh = () => {
    if (hasSearched && searchTerm.trim()) {
      // Re-run the search with current criteria
      searchMaids();
    } else {
      setSuccessMessage('Enter a search term and click Search to find technicians.');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleVerifyMaid = async (updatedMaid) => {
    // Update the maids list
    setMaids(prev => prev.map(maid => 
      maid.id === updatedMaid.id ? updatedMaid : maid
    ));
    setFilteredMaids(prev => prev.map(maid => 
      maid.id === updatedMaid.id ? updatedMaid : maid
    ));
    
    console.log('Maid verified successfully');
  };

  const handleInvitationSent = (invitationData) => {
    setSuccessMessage(`Invitation sent successfully to ${invitationData.firstName} ${invitationData.lastName}!`);
    
    // Clear success message after 5 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 5000);
  };

  const handleRejectMaid = async (maidProfile, reason) => {
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
      
      const response = await axios.patch(
        `${apiBase}/api/maids/${maidProfile.id}/reject`,
        { reason },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        // Update the maid's status locally
        const updatedMaid = {
          ...maidProfile,
          onboardingStatus: 'rejected',
          rejectionReason: reason,
          rejectedAt: new Date().toISOString()
        };
        
        // Update both maids arrays
        setMaids(prev => prev.map(maid => 
          maid.id === maidProfile.id ? updatedMaid : maid
        ));
        setFilteredMaids(prev => prev.map(maid => 
          maid.id === maidProfile.id ? updatedMaid : maid
        ));
        
        setSuccessMessage(`${maidProfile.firstName} ${maidProfile.lastName} has been rejected successfully.`);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        throw new Error(response.data.error || 'Failed to reject technician');
      }
    } catch (error) {
      console.error('Error rejecting maid:', error);
      
      // Extract error message for user display
      const errorMsg = error.response?.data?.error || 
                       error.response?.data?.message || 
                       error.message || 
                       'Failed to reject technician application. Please try again.';
      
      setErrorMessage(errorMsg);
      
      // Clear error message after 8 seconds
      setTimeout(() => {
        setErrorMessage('');
      }, 8000);
      
      throw error; // Re-throw so the component can handle the error
    }
  };

  return (
    <AdminPageLayout
      title="Technician Crew Management"
      subtitle="Manage your entire crew of technicians. Edit profiles, toggle status, and view detailed information."
      maxWidth="lg"
    >
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={() => setAddressDialogOpen(true)}
          >
            Request Missing Addresses
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite Technician
          </Button>
        </Stack>
      </Box>

      {/* Search Interface */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search Technicians
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Enter a name or email to search for technicians. No results will be shown until you perform a search.
        </Typography>
        
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300, flex: 1 }}
          />
          
          <Button
            variant="contained"
            onClick={searchMaids}
            disabled={loading || !searchTerm.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <Search />}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={handleClearSearch}
            disabled={!hasSearched}
          >
            Clear
          </Button>
        </Stack>

        {/* Filters */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
          <FormControl>
            <FormLabel id="status-filter-label" sx={{ mb: 1, fontSize: '0.875rem' }}>
              Filter by Status
            </FormLabel>
            <RadioGroup
              aria-labelledby="status-filter-label"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              row
            >
              <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
              <FormControlLabel value="incomplete" control={<Radio size="small" />} label="Incomplete" />
              <FormControlLabel value="pending_review" control={<Radio size="small" />} label="Pending Review" />
              <FormControlLabel value="approved" control={<Radio size="small" />} label="Approved" />
              <FormControlLabel value="rejected" control={<Radio size="small" />} label="Rejected" />
            </RadioGroup>
          </FormControl>

          <FormControl>
            <FormLabel id="aplayer-filter-label" sx={{ mb: 1, fontSize: '0.875rem' }}>
              Filter by Performance
            </FormLabel>
            <RadioGroup
              aria-labelledby="aplayer-filter-label"
              value={aPlayerFilter}
              onChange={(e) => setAPlayerFilter(e.target.value)}
              row
            >
              <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
              <FormControlLabel value="aplayer" control={<Radio size="small" />} label="⭐ A-Players" />
              <FormControlLabel value="regular" control={<Radio size="small" />} label="Regular" />
            </RadioGroup>
          </FormControl>
        </Stack>
      </Paper>

      {/* Results Summary - only show if searched */}
      {hasSearched && (
        <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
          <Chip label={`Found: ${filteredMaids.length}`} color="primary" />
          <Chip label={`Approved: ${filteredMaids.filter(m => m.onboardingStatus === 'approved').length}`} color="success" />
          <Chip label={`Pending Review: ${filteredMaids.filter(m => m.onboardingStatus === 'pending_review').length}`} color="warning" />
          <Chip label={`Incomplete: ${filteredMaids.filter(m => m.onboardingStatus === 'incomplete').length}`} color="default" />
          <Chip label={`Rejected: ${filteredMaids.filter(m => m.onboardingStatus === 'rejected').length}`} color="error" />
          <Chip 
            label={`⭐ A-Players: ${filteredMaids.filter(m => m.isAPlayer).length}`} 
            sx={{ 
              bgcolor: '#FFF3E0', 
              color: '#FF9800',
              fontWeight: 600 
            }} 
          />
        </Stack>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Searching for technicians...</Typography>
        </Box>
      ) : !hasSearched ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Search sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" color="textSecondary" gutterBottom>
            Search for Technicians
          </Typography>
          <Typography color="textSecondary">
            Enter a name or email address above to find and manage technicians.
          </Typography>
        </Paper>
      ) : filteredMaids.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No technicians found matching "{searchTerm}". Try a different search term or adjust your filters.
          </Typography>
        </Paper>
      ) : (
        <List>
          {filteredMaids.map((maid) => (
            <ListItem key={maid.id} divider>
              <Avatar sx={{ mr: 2 }} src={maid.profilePicture}>
                {maid.firstName?.[0] || 'M'}
              </Avatar>
              <ListItemText
                primary={
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography variant="body1" fontWeight={500}>
                      {maid.firstName} {maid.lastName}
                    </Typography>
                    <MaidStatusToggle
                      maidProfile={maid}
                      onStatusChange={handleStatusChange}
                      variant="chip"
                      showLabel={false}
                      confirmAction={false}
                    />
                    <OnboardingStatusChip
                      maidProfile={maid}
                      showProgress={true}
                      size="small"
                    />
                    {maid.isAPlayer && (
                      <Chip
                        icon={<span style={{ fontSize: '12px' }}>⭐</span>}
                        label="A-Player"
                        size="small"
                        sx={{
                          bgcolor: '#FFF3E0',
                          color: '#FF9800',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          '& .MuiChip-icon': {
                            fontSize: '12px'
                          }
                        }}
                      />
                    )}
                  </Stack>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {maid.email} • Tel: {maid.phoneNumber}
                      {maid.residentialAddress && ` • ${maid.residentialAddress}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                      {maid._isLightData ? (
                        <Chip 
                          label="Quick View" 
                          size="small" 
                          variant="outlined" 
                          color="info"
                          sx={{ fontSize: '0.7rem', height: '18px' }}
                        />
                      ) : (
                        `Last updated: ${maid.updatedAt ? new Date(maid.updatedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'N/A'}`
                      )}
                    </Typography>
                    <Stack direction="row" spacing={1} mt={0.5} alignItems="center" flexWrap="wrap">
                      {maid.services?.slice(0, 2).map((service, index) => (
                        <Chip key={index} label={service} size="small" variant="outlined" />
                      ))}
                      {maid.services?.length > 2 && (
                        <Chip label={`+${maid.services.length - 2} more`} size="small" variant="outlined" />
                      )}
                      
                      {/* Supplies indicator */}
                      {maid.bringsSupplies && (
                        <Chip 
                          label="🧽 Brings Supplies" 
                          size="small" 
                          color="info"
                          variant="filled"
                          sx={{ 
                            fontSize: '0.7rem', 
                            height: '22px',
                            bgcolor: '#E3F2FD',
                            color: '#1976D2',
                            fontWeight: 500
                          }}
                        />
                      )}
                      
                      {/* Completion percentage indicator */}
                      <CompletionIndicator maid={maid} />
                    </Stack>
                    <TechnicianPortalAccess
                      technician={{
                        userId: maid.userId || maid.user?.id,
                        firstName: maid.firstName,
                        lastName: maid.lastName,
                        email: maid.email,
                        isActive: maid.isActive,
                      }}
                      status={portalStatuses[maid.userId || maid.user?.id] || (maid.lastLogin ? { lastLogin: maid.lastLogin } : undefined)}
                      onInvited={(userId, invitedAt) => setPortalStatuses((prev) => ({
                        ...prev,
                        [userId]: { ...(prev[userId] || {}), lastInvitedAt: invitedAt },
                      }))}
                    />
                  </Box>
                }
              />
              <Box>
                {loadingFullData.has(maid.id) ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="caption" color="text.secondary">
                      Loading details...
                    </Typography>
                  </Box>
                ) : (
                  <MaidQuickActions
                    maidProfile={maid}
                    onProfileUpdate={handleProfileUpdate}
                    onStatusChange={handleStatusChange}
                    onViewDetails={handleViewDetails}
                    onRejectMaid={handleRejectMaid}
                    variant="inline"
                    showStatusToggle={false}
                  />
                )}
              </Box>
            </ListItem>
          ))}
        </List>
      )}
      
      {/* Enhanced Editable Maid Details Modal */}
      <EditableMaidDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        maidProfile={selectedMaid}
        onProfileUpdate={handleProfileUpdate}
        onVerifyMaid={handleVerifyMaid}
        comprehensiveData={comprehensiveData}
        loadingComprehensive={loadingComprehensive}
      />

      <RequestAddressesDialog
        open={addressDialogOpen}
        onClose={() => setAddressDialogOpen(false)}
        onSent={(count) => {
          setSuccessMessage(`Address request queued for ${count} technician(s). Emails go out over the next few minutes.`);
          setTimeout(() => setSuccessMessage(''), 8000);
        }}
      />

      {/* Note: InviteMaidDialog is handled by AdminPageLayout */}
    </AdminPageLayout>
  );
};

export default MaidCrewManagement; 